// ─── THE vertical crosshair. One position, one look, every pane. ───────────
// The main chart's line is a DOM div; the CVD and Delta Volume Pro panes
// paint theirs on canvas. They used to agree only on paper: the DOM line was
// a 1.5px dashed LEFT BORDER (paints [x, x+w], Chrome rounds the width to
// whole device pixels and picks its own dash) while the panes stroked a
// 1.5px [6,4] line CENTERED on x (paints [x-0.75, x+0.75], antialiased across
// three columns). Same x in, ~0.75px apart and visibly different out.
//
// Both now go through crosshairGeom(): same whole-pixel snap, same width,
// same [6,4] dash, so the main line and every subgraph line light exactly
// the same device columns. Callers pass the raw cursor x in the shared
// plot coordinate (mouseXRef / updateCrosshair's x); nobody re-derives it.
export const CROSSHAIR_COLOR = 'rgba(200, 218, 255, 0.35)'
export const CROSSHAIR_WIDTH = 1            // CSS px (see crosshairGeom)
export const CROSSHAIR_DASH = [6, 4]        // on, off (CSS px)

// x (CSS px, plot coordinate) → { left, width, center }, snapped to WHOLE
// CSS pixels: the line fills the column that contains x, [floor(x), +1).
// Whole CSS px is the only grid the two renderers agree on — Chrome snaps a
// DOM box's left/width to whole CSS px, while a dpr-scaled canvas fillRect
// lands exactly where asked. Tested at dpr 1, 1.25, 1.5 and 2: integer
// left + integer width light identical device pixels in both; anything
// fractional (1.5px, device-px snapping) does not. 1px is the ink the old
// 1.5px dashed border actually rendered with (Chrome thinned/antialiased it).
export function crosshairGeom(x) {
  const left = Math.floor(x)
  return { left, width: CROSSHAIR_WIDTH, center: left + CROSSHAIR_WIDTH / 2 }
}

// Static style for the main chart's DOM line (position set by placeCrosshairEl).
export const CROSSHAIR_DOM_STYLE = {
  background: `repeating-linear-gradient(to bottom, ${CROSSHAIR_COLOR} 0px, ${CROSSHAIR_COLOR} ${CROSSHAIR_DASH[0]}px, transparent ${CROSSHAIR_DASH[0]}px, transparent ${CROSSHAIR_DASH[0] + CROSSHAIR_DASH[1]}px)`,
}

export function placeCrosshairEl(el, x) {
  const g = crosshairGeom(x)
  el.style.width = `${g.width}px`
  el.style.transform = `translateX(${g.left}px)`
}

// Canvas twin of the DOM line: dashes as pixel-aligned fillRects (no stroke
// antialiasing), same geometry. Assumes ctx is already scaled by dpr.
export function drawVCrosshair(ctx, x, H, maxX = Infinity) {
  if (!(x >= 0) || x > maxX) return
  const g = crosshairGeom(x)
  const [on, off] = CROSSHAIR_DASH
  ctx.save()
  ctx.fillStyle = CROSSHAIR_COLOR
  for (let y = 0; y < H; y += on + off) ctx.fillRect(g.left, y, g.width, Math.min(on, H - y))
  ctx.restore()
}

// ─── Same-frame sync: one call moves every pane's line ────────────────────
// The subgraph lines used to be painted in each pane's rAF draw loop from
// mouseXRef, so they trailed the chart's DOM line by ≥1 frame (more when a
// draw ran long). Now every pane's line is a DOM element too, registered
// here under the chart's shared mouseXRef (one group per chart instance, so
// split / sol+fut charts never move each other's lines), and
// placeCrosshairs(key, x) moves the whole group synchronously from the same
// pointer handler. -1 (or any x < 0) hides the group.
const _groups = new WeakMap()   // key (mouseXRef object) → { els:Set, x }
const _group = (key) => {
  let g = _groups.get(key)
  if (!g) { g = { els: new Set(), x: -1 }; _groups.set(key, g) }
  return g
}
const _apply = (el, x) => {
  if (!(x >= 0)) { el.style.display = 'none'; return }
  el.style.display = 'block'
  placeCrosshairEl(el, x)
}
export function placeCrosshairs(key, x) {
  if (!key) return
  const g = _group(key)
  g.x = x
  for (const el of g.els) _apply(el, x)
}
// Returns the unregister function (use as a useEffect cleanup).
export function registerCrosshairEl(key, el) {
  if (!key || !el) return () => {}
  const g = _group(key)
  g.els.add(el)
  _apply(el, g.x)   // mount mid-hover: show at the current position at once
  return () => { g.els.delete(el) }
}

// Inline style for a pane's line element (position/visibility are set by
// placeCrosshairs). Parent must be position:relative and clip to the plot.
export const CROSSHAIR_PANE_STYLE = {
  display: 'none', position: 'absolute', top: 0, left: 0, width: 0, height: '100%',
  ...CROSSHAIR_DOM_STYLE, pointerEvents: 'none',
}
